import type { QueueViewKey } from '../../../types/encounterWork';
import type { ColumnDef } from '../shared';

import type { HomeQueueDefinition } from './homeQueues';

/**
 * Each queue's own column set for Home's table (GDN-4068, full width since
 * GDN-4085's summary strip).
 *
 * Every set starts with the date and who, and adds only columns the Claims
 * tab already shows for the same queue, from data the row already carries,
 * under the Claims tab's own labels, so a claim reads the same on both
 * surfaces. The patient column takes the organization's word for a patient.
 *
 * `minmax(0, …)` and not a bare `fr`: a grid track's automatic minimum is its
 * MIN-CONTENT, and every cell here is nowrap text, so a bare `fr` lets the
 * longest sentence push the column past the table's edge and clip square
 * instead of ellipsizing. Only the date and the money have fixed widths, so
 * the table still fits a narrow screen.
 *
 * On a phone the name columns squeeze to their badges and the headers crowd
 * each other; accepted because billers use Home on desktop.
 */

type ColumnSet = (queue: HomeQueueDefinition, subjectLabel: string) => ColumnDef[];

const date: ColumnDef = { key: 'service_date', label: 'Date', required: true, width: '68px' };

const patient = (subjectLabel: string, width = 'minmax(0, 1.1fr)'): ColumnDef => ({
  key: 'patient_name',
  label: subjectLabel,
  required: true,
  width,
});

const payer = (width = 'minmax(0, 0.8fr)'): ColumnDef => ({
  key: 'payer_name',
  label: 'Payer',
  width,
});

/** The queue's own column, headed in the queue's own words. */
const detail = (queue: HomeQueueDefinition, width = 'minmax(0, 1.4fr)'): ColumnDef => ({
  key: 'detail',
  label: queue.detailColumnLabel,
  required: true,
  width,
});

/** The rendering provider, as the Claims tab's Provider column shows it. */
const provider: ColumnDef = { key: 'provider_name', label: 'Provider', width: 'minmax(0, 1fr)' };

/** The claim's total charge. */
const charge: ColumnDef = { align: 'right', key: 'charge', label: 'Charge', width: '90px' };

const HOME_QUEUE_COLUMNS: Record<QueueViewKey, ColumnSet> = {
  /* The payer's answer gets the room. No Provider, as on the Claims tab's
     fix view: nobody calls the rendering clinician about a denial. */
  fix: (queue, subjectLabel) => [
    date,
    patient(subjectLabel, 'minmax(0, 1.3fr)'),
    payer('minmax(0, 1.2fr)'),
    detail(queue, 'minmax(0, 2.4fr)'),
    charge,
  ],
  review: (queue, subjectLabel) => [
    date,
    patient(subjectLabel),
    provider,
    payer(),
    detail(queue, 'minmax(0, 2fr)'),
    charge,
  ],
  /* The secondary's four facts, each in the Claims tab's own column for it.
     No Provider, as on the Claims tab's Bill secondary view. */
  bill_secondary: (_queue, subjectLabel) => [
    date,
    patient(subjectLabel),
    payer(),
    {
      key: 'secondary_payer',
      label: 'Secondary payer',
      required: true,
      width: 'minmax(0, 0.9fr)',
    },
    { align: 'right', key: 'secondary_balance', label: 'Balance', required: true, width: '90px' },
    { key: 'era_age', label: 'Days since ERA', width: 'minmax(0, 0.7fr)' },
    { key: 'secondary_blockers', label: 'Blockers', required: true, width: 'minmax(0, 1.4fr)' },
  ],
  /* The queue's own column is already the charge. */
  submit: (queue, subjectLabel) => [date, patient(subjectLabel), provider, payer(), detail(queue)],
  awaiting_payer: (queue, subjectLabel) => [
    date,
    patient(subjectLabel),
    provider,
    payer(),
    detail(queue),
    charge,
  ],
};

/** The columns for a queue's table on Home. */
export const homeQueueColumns = (queue: HomeQueueDefinition, subjectLabel: string): ColumnDef[] =>
  HOME_QUEUE_COLUMNS[queue.key](queue, subjectLabel);
